/* eslint-disable react-hooks/exhaustive-deps */
// RODAPÉ "Larissa" (rodape = 11): rodapé do modelo "Larissa" — fundo na cor escura do tenant, 3 faixas:
//  1) CHAMADA central em serifa em duas tonalidades + CONTATO do tenant no lugar onde a referência tem
//     a newsletter (que não temos): o número em um campo translúcido + botão branco "Chamar no
//     WhatsApp" (ou "Ligar"), e os demais telefones/e-mail em linha;
//  2) marca: logo (em placa clara — o cadastro não tem versão clara) com um traço fino, navegação do
//     tenant em linha e, à direita, "Siga nas redes sociais" com as redes em CÍRCULOS de 40px;
//  3) legal: © empresa, CRECI, CNPJ e endereço; política de privacidade/termos; html_rodape e selo
//     ImobTotal (gate dentro do componente).
// "Pesquisas mais populares" (Popular) segue como nos demais rodapés (!hidePopular).
// prefers-reduced-motion: sem transições. Default export = ErrorBoundary com fallback.
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import type { RodapeSectionProps } from '../../registry/types';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import LinhaSeoRodape from '../rodape-seo/LinhaSeoRodape';
import Popular from '../populares/Popular';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';
import { contatosDoTenant, linkWhatsAppDoTenant, nomeDaEmpresa, redesDoTenant, paletaLarissa, useLogoSemPlaca, useMovimentoReduzido, varsLarissa } from '../larissa/larissa';
import type { ItemMenu } from '../larissa/larissa';
import '../larissa/LarissaFonte.module.css';
import styles from './LarissaFooter.module.css';

const ICONE_REDE: Record<string, React.ComponentType<{ 'aria-hidden'?: boolean }>> = {
    Instagram: FaInstagram,
    Facebook: FaFacebook,
    Youtube: FaYoutube,
    Tiktok: FaTiktok,
    Linkedin: FaLinkedin,
};
const LEGAL_RE = /privacidade|lgpd|termos/i;

const LarissaFooterInner: React.FC<RodapeSectionProps> = ({ site: siteProp, origin, codsite, allRodape, hidePopular, allCities, allCitiesTipes }) => {
    // MULTI-REGIÃO: contatos trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const reduzido = useMovimentoReduzido();
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const withCodsite = (path: string) => (isMounted ? appendCodsiteQuery(path, codsiteQuery) : path);

    // ---- dados do tenant ----
    const empresa = nomeDaEmpresa(site);
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    // logo clara (feita p/ fundo escuro) vai direto sobre a base, sem a placa branca — decidido depois de montar
    const semPlaca = useLogoSemPlaca(logoSrc, paletaLarissa(site).base);
    const { phones, ctaPhone, ctaIsWhats, ctaHref } = contatosDoTenant(site, 4);
    const ctaNumero = MaskPhone(ctaPhone?.tel || '');
    const outros = phones.filter((p) => p !== ctaPhone);
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const redes = redesDoTenant(site);
    const creci = site?.Dados?.creci;
    const cnpj = site?.Dados?.cnpj;
    const endereco = [site?.Dados?.endereco, [site?.Dados?.bairro, site?.Dados?.cidade].filter(Boolean).join(' - '), site?.Dados?.cep ? `CEP ${site.Dados.cep}` : ''].filter(Boolean).join(' · ');

    const menusDisponiveis = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo);
    const paraItem = (m: any): ItemMenu => ({
        label: m.titulo,
        href: m?.link ? m.link : `/menu/${m.titulo}`,
        external: Boolean(m?.link && !String(m.link).startsWith('/')),
    });
    // navegação em linha: transações + menus do tenant (sem os legais) + blog — só links que o site já tem
    const navegacao: ItemMenu[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        navegacao.push({ label: low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t, href: `/imoveis/${low}/-/-/-/-` });
    });
    menusDisponiveis
        .filter((m: any) => !LEGAL_RE.test(String(m.titulo)))
        .slice(0, 8)
        .forEach((m: any) => navegacao.push(paraItem(m)));
    if (allDataHome?.blogs?.[0]?.id) navegacao.push({ label: 'Blog', href: '/blog' });

    const legais: ItemMenu[] = menusDisponiveis.filter((m: any) => LEGAL_RE.test(String(m.titulo))).map(paraItem);
    if (!legais.some((i) => /privacidade|lgpd/i.test(i.label))) legais.push({ label: 'Política de privacidade', href: '/politica' });
    if (!legais.some((i) => /termos/i.test(i.label))) legais.push({ label: 'Termos de uso', href: '/termos' });

    const rastrear = (metodo: 'whatsapp' | 'phone' | 'email') => () => {
        if (metodo !== 'email') trackLead(site, null); // whatsapp_click (GTM/GA), como no rodapé clássico (sem imóvel = sem Meta)
        trackMetaEvent(site, 'Contact', { content_name: 'Chamada do rodapé', contact_method: metodo, content_category: 'footer_contact' });
    };

    const renderLink = (item: ItemMenu, idx: number, cls: string) =>
        item.external ? (
            <a key={idx} href={item.href} rel="noopener noreferrer" className={cls}>
                {item.label}
            </a>
        ) : (
            <Link key={idx} prefetch={false} href={withCodsite(item.href)} className={cls}>
                {item.label}
            </Link>
        );

    return (
        <>
            {/* "Pesquisas mais populares" no visual do Larissa em todas as páginas (Daniel 15/10: "personalizar o layout
                de PESQUISAS MAIS POPULARES") — a casca leva as vars do modelo e as regras de .populares no módulo do rodapé. */}
            {!hidePopular && (
                <div className={styles.populares} style={varsLarissa(site)}>
                    <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin} />
                </div>
            )}
            {/* BUILDER: âncora estável do bloco "rodape". */}
            <footer className={`${styles.footer} ${reduzido ? styles.semMovimento : ''}`} data-builder-section="rodape" style={varsLarissa(site)}>
                {/* ---------- 1) chamada + contato ---------- */}
                <div className={styles.chamada}>
                    <h2 className={styles.titulo}>
                        Fale com <span className={styles.suave}>quem entende</span> do mercado imobiliário:
                        <br className={styles.quebra} /> converse com <span className={styles.suave}>{empresa || 'a nossa equipe'}</span>.
                    </h2>
                    <p className={styles.texto}>
                        <strong>Atendimento próximo</strong> para comprar, alugar ou anunciar o seu imóvel.
                    </p>

                    {ctaPhone ? (
                        <div className={styles.contato}>
                            <span className={styles.numero}>
                                {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                <span>{ctaNumero}</span>
                            </span>
                            <a
                                className={styles.botao}
                                href={ctaHref}
                                target={ctaIsWhats ? '_blank' : undefined}
                                rel="noopener noreferrer"
                                aria-label={ctaIsWhats ? `Chamar no WhatsApp ${ctaNumero}` : `Ligar para ${ctaNumero}`}
                                onClick={rastrear(ctaIsWhats ? 'whatsapp' : 'phone')}
                            >
                                {ctaIsWhats ? 'Chamar no WhatsApp' : 'Ligar agora'}
                            </a>
                        </div>
                    ) : null}

                    {outros.length > 0 || email ? (
                        <ul className={styles.outros}>
                            {outros.map((p, idx) => (
                                <li key={idx}>
                                    <a className={styles.outro} href={p.whats ? linkWhatsAppDoTenant(site, p.tel) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`} target={p.whats ? '_blank' : undefined} rel="noopener noreferrer" onClick={rastrear(p.whats ? 'whatsapp' : 'phone')}>
                                        {p.whats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                        <span>
                                            {MaskPhone(p.tel || '')}
                                            {p.nome ? ` — ${p.nome}` : ''}
                                        </span>
                                    </a>
                                </li>
                            ))}
                            {email ? (
                                <li>
                                    <a className={styles.outro} href={`mailto:${email}`} onClick={rastrear('email')}>
                                        <span>{email}</span>
                                    </a>
                                </li>
                            ) : null}
                        </ul>
                    ) : null}
                </div>

                <span className={styles.divisa} aria-hidden="true" />

                {/* ---------- 2) marca + redes ---------- */}
                <div className={styles.marca}>
                    <div className={styles.marcaLogo}>
                        <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                            {logoSrc ? (
                                <span className={`${styles.placa} ${semPlaca ? styles.placaSemFundo : ''}`}>
                                    <Image src={logoSrc} alt={`Imobiliária ${empresa}`} className={styles.logo} width={220} height={72} unoptimized />
                                </span>
                            ) : (
                                <span className={styles.logoTexto}>{empresa}</span>
                            )}
                        </Link>
                        <span className={styles.fio} aria-hidden="true" />
                    </div>
                    {redes.length > 0 ? (
                        <div className={styles.social}>
                            <p className={styles.socialTexto}>Siga nas redes sociais e acompanhe as novidades.</p>
                            <div className={styles.redes}>
                                {redes.map((r) => {
                                    const Icone = ICONE_REDE[r.rede];
                                    return (
                                        <a key={r.rede} href={r.href} target="_blank" rel="noopener noreferrer" className={styles.rede} aria-label={r.rede} title={r.rede}>
                                            {Icone ? <Icone aria-hidden={true} /> : r.rede}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>

                {navegacao.length > 0 ? (
                    <nav className={styles.nav} aria-label="Navegação do rodapé">
                        {navegacao.map((item, idx) => renderLink(item, idx, styles.navLink))}
                    </nav>
                ) : null}

                {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup/tela —
                    tenant sem regiões não muda um byte). Visual do Larissa via [data-el] no CSS. */}
                <FiliaisRodape site={site} fundo={paletaLarissa(site).base} cor="#ffffff" />

                {/* SEO: cidades · tipos · empreendimentos do rodapé clássico, em linha discreta (16/10) */}
                <LinhaSeoRodape
                    site={site}
                    allCities={allCities}
                    allCitiesTipes={allCitiesTipes}
                    allRodape={allRodape}
                    withCodsite={withCodsite}
                    jaTem={[...navegacao, ...legais].map((i) => i.href)}
                    className={styles.linhaSeo}
                />

                {/* ---------- 3) legal ---------- */}
                <div className={styles.legal}>
                    <div className={styles.legalInner}>
                        <p className={styles.direitos}>
                            © {empresa || 'Imobiliária'}
                            {creci ? ` · CRECI ${creci}` : ''}
                            {cnpj ? ` · CNPJ ${cnpj}` : ''}
                            {endereco ? <span className={styles.endereco}>{endereco}</span> : null}
                        </p>
                        <div className={styles.legais}>{legais.map((item, idx) => renderLink(item, idx, styles.legalLink))}</div>
                    </div>
                    {site?.html_rodape ? <div className={styles.htmlRodape} dangerouslySetInnerHTML={{ __html: site?.html_rodape }} /> : null}
                    {/* Selo ImobTotal: respeita copyright + EsconderRodapeSobressai (gate dentro do componente). */}
                    <div className={styles.selo}>
                        <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria fonte="13px" />
                    </div>
                </div>
            </footer>
        </>
    );
};

class LarissaFooter extends React.Component<RodapeSectionProps, { hasError: boolean }> {
    constructor(props: RodapeSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <LarissaFooterInner {...this.props} />;
    }
}

export default LarissaFooter;
